import { useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Toasts } from './Toasts';
import { useToasts } from './useToasts';
import { ToastContext } from './toastContext';

interface IToastProviderProps {
  children: ReactNode;
}

const onFullscreenChange = (notify: () => void) => {
  document.addEventListener('fullscreenchange', notify);
  return () => document.removeEventListener('fullscreenchange', notify);
};

/** Where the stack is drawn: the fullscreen element while there is one (the
 *  Fullscreen API paints nothing outside it — a fullscreen Gantt would hide a
 *  body-level toast), the body otherwise. */
const toastHost = (): Element => document.fullscreenElement ?? document.body;

/** The app's one shared toast stack: the queue, offered to every descendant
 *  through `useToast`, and its host, portalled above any drawer. */
export function ToastProvider({ children }: IToastProviderProps) {
  const { toasts, push, dismiss } = useToasts();
  const host = useSyncExternalStore(onFullscreenChange, toastHost);
  return (
    <ToastContext.Provider value={push}>
      {children}
      {createPortal(<Toasts toasts={toasts} onDismiss={dismiss} />, host)}
    </ToastContext.Provider>
  );
}
